import i18n, { getBackendLanguage } from '@assets/i18n/config';
import type { FinancialProduct } from '@interfaces/financialProduct';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { sanitizeCsvRow, sanitizeForDisplay } from '@utils/csvSanitization';
import { validateCsv } from '@utils/csvValidation';
import Papa from 'papaparse';

export type UnrecognizedSecurity = {
  ticker: string;
  targetWeight: number;
  rowIndex: number;
  row: Record<string, string>;
};

const SUPPORT_COLORS = [
  'supportOne',
  'supportTwo',
  'supportThree',
  'supportFour',
  'supportFive',
  'supportSix',
] as const;

const FULL_PERCENTAGE = 100;
const ROW_INDEX_OFFSET = 2;
const MIN_TARGET_WEIGHT = 0;
const MIN_TAX_RANK = 1;
const RADIX_DECIMAL = 10;
const MAX_ERRORS_DISPLAY = 5;
const BYTES_PER_MB = 1024 * 1024;
const DECIMAL_PLACES = 1;
const INITIAL_SUM = 0;
const DECIMAL_PLACES_DISPLAY = 2;

export const extractUniqueTickers = (
  rows: Record<string, string>[],
): Set<string> => {
  const uniqueTickers = new Set<string>();
  rows.forEach((row) => {
    const identifier = row['securityIdentifier(Ticker)']?.trim();
    if (identifier) {
      uniqueTickers.add(identifier.toUpperCase());
    }
  });
  return uniqueTickers;
};

export const buildSecurityMap = (
  securities: FinancialProduct[],
): Map<string, FinancialProduct> => {
  const securityMap = new Map<string, FinancialProduct>();
  securities.forEach((sec) => {
    if (sec.ticker) {
      securityMap.set(sec.ticker.toUpperCase(), sec);
    }
  });
  return securityMap;
};

export const formatAvailableSecurities = (
  securities: FinancialProduct[],
): Array<{ id: string; ticker: string; name: string }> => {
  return securities
    .filter(
      (product): product is FinancialProduct & { id: string } => !!product.id,
    )
    .map((product) => ({
      id: product.id,
      ticker: product.ticker || '',
      name:
        product.translatedName?.[getBackendLanguage()] || product.ticker || '',
    }));
};

export const validateCsvRow = (
  row: Record<string, string>,
  index: number,
): {
  identifier?: string;
  targetWeight?: number;
  taxRank?: number;
  error?: string;
} => {
  const identifier = row['securityIdentifier(Ticker)']?.trim();
  const targetWeight = parseFloat(row.targetWeight?.trim() || '0');
  const taxRankStr = row.taxRank?.trim();

  if (!identifier) {
    const error = i18n.t(
      'modelPortfolios:importCsv.errors.rowMissingIdentifier',
      {
        rowNumber: index + ROW_INDEX_OFFSET,
      },
    );
    return { error };
  }

  if (
    Number.isNaN(targetWeight) ||
    targetWeight < MIN_TARGET_WEIGHT ||
    targetWeight > FULL_PERCENTAGE
  ) {
    const error = i18n.t(
      'modelPortfolios:importCsv.errors.invalidTargetWeight',
      {
        rowNumber: index + ROW_INDEX_OFFSET,
        ticker: sanitizeForDisplay(identifier),
        targetWeight: sanitizeForDisplay(row.targetWeight),
        min: MIN_TARGET_WEIGHT,
        max: FULL_PERCENTAGE,
      },
    );
    return {
      error,
    };
  }

  let taxRank: number | undefined;
  if (taxRankStr) {
    taxRank = parseInt(taxRankStr, RADIX_DECIMAL);
    if (Number.isNaN(taxRank) || taxRank < MIN_TAX_RANK) {
      const error = i18n.t('modelPortfolios:importCsv.errors.invalidTaxRank', {
        rowNumber: index + ROW_INDEX_OFFSET,
        ticker: sanitizeForDisplay(identifier),
        taxRank: sanitizeForDisplay(row.taxRank),
        min: MIN_TAX_RANK,
      });
      return {
        error,
      };
    }
  }

  return { identifier, targetWeight, taxRank };
};

export const processCsvRows = (
  rows: Record<string, string>[],
  securityMap: Map<string, FinancialProduct>,
): {
  securities: Security[];
  errors: string[];
  providedTaxRanks: number[];
  missingSecurities: UnrecognizedSecurity[];
} => {
  const securities: Security[] = [];
  const errors: string[] = [];
  const providedTaxRanks: number[] = [];
  const missingSecurities: UnrecognizedSecurity[] = [];

  rows.forEach((row, index) => {
    const validation = validateCsvRow(row, index);

    if (validation.error) {
      errors.push(validation.error);
      return;
    }

    const { identifier, targetWeight, taxRank } = validation;
    if (!identifier || targetWeight === undefined) return;

    if (taxRank) {
      providedTaxRanks.push(taxRank);
    }

    const identifierKey = identifier.toUpperCase();
    const matchedSecurity = securityMap.get(identifierKey);

    if (!matchedSecurity) {
      missingSecurities.push({
        ticker: identifier,
        targetWeight,
        rowIndex: index,
        row,
      });
      return;
    }

    securities.push({
      id: matchedSecurity.id,
      financialProduct: matchedSecurity,
      percentage: targetWeight,
      taxRank: taxRank || 0,
      isCashEquivalent: false,
      color: SUPPORT_COLORS[securities.length % SUPPORT_COLORS.length],
    });
  });

  return { securities, errors, providedTaxRanks, missingSecurities };
};

export const validateTaxRanks = (providedTaxRanks: number[]): string | null => {
  const uniqueProvidedTaxRanks = new Set(providedTaxRanks);
  if (providedTaxRanks.length !== uniqueProvidedTaxRanks.size) {
    return i18n.t('modelPortfolios:importCsv.errors.duplicateTaxRanks');
  }
  return null;
};

export const assignMissingTaxRanks = (
  securities: Security[],
  providedTaxRanks: number[],
): void => {
  const usedTaxRanks = new Set(providedTaxRanks);
  let nextAvailableTaxRank = MIN_TAX_RANK;

  securities.forEach((security) => {
    if (security.taxRank === MIN_TARGET_WEIGHT) {
      while (usedTaxRanks.has(nextAvailableTaxRank)) {
        nextAvailableTaxRank++;
      }
      security.taxRank = nextAvailableTaxRank;
      usedTaxRanks.add(nextAvailableTaxRank);
      nextAvailableTaxRank++;
    }
  });
};

export const formatImportErrors = (errors: string[]): string => {
  const displayedErrors = errors.slice(0, MAX_ERRORS_DISPLAY);
  const remainingCount = errors.length - MAX_ERRORS_DISPLAY;

  let message =
    i18n.t('modelPortfolios:importCsv.errors.failedToImportPrefix') +
    '\n' +
    displayedErrors.join('\n');
  if (remainingCount > 0) {
    message +=
      '\n' +
      i18n.t(
        remainingCount === 1
          ? 'modelPortfolios:importCsv.errors.moreErrorsOne'
          : 'modelPortfolios:importCsv.errors.moreErrorsMany',
        {
          count: remainingCount,
        },
      );
  }

  return message;
};

export const deduplicateTaxRanks = (securities: Security[]): Security[] => {
  const usedTaxRanks = new Set<number>();
  let nextAvailable = 1;

  return securities.map((s) => {
    if (!s.taxRank || s.financialProduct.isCash) return s;

    if (!usedTaxRanks.has(s.taxRank)) {
      usedTaxRanks.add(s.taxRank);
      return s;
    }

    while (usedTaxRanks.has(nextAvailable)) nextAvailable++;
    usedTaxRanks.add(nextAvailable);
    return { ...s, taxRank: nextAvailable++ };
  });
};

export const validateTotalAllocation = (
  securities: Security[],
  tolerance: number,
): string | null => {
  const totalPercentage = securities.reduce(
    (sum, sec) => sum + sec.percentage,
    INITIAL_SUM,
  );

  if (Math.abs(totalPercentage - FULL_PERCENTAGE) > tolerance) {
    return i18n.t('modelPortfolios:importCsv.errors.totalAllocation', {
      total: FULL_PERCENTAGE,
      current: totalPercentage.toFixed(DECIMAL_PLACES_DISPLAY),
    });
  }

  return null;
};

export const validateFileSize = (file: File, maxSizeMB: number): void => {
  if (file.size === 0) {
    throw new Error(i18n.t('modelPortfolios:importCsv.errors.emptyFile'));
  }

  if (file.size > maxSizeMB * BYTES_PER_MB) {
    const maxSize = maxSizeMB.toFixed(DECIMAL_PLACES);
    const fileSize = (file.size / BYTES_PER_MB).toFixed(DECIMAL_PLACES);
    const error = i18n.t('modelPortfolios:importCsv.errors.fileTooLarge', {
      fileSize,
      maxSize,
    });
    throw new Error(error);
  }
};

export const parseCsvFile = (
  file: File,
  options?: {
    maxRows?: number;
    onProgress?: (rowsProcessed: number) => void;
  },
): Promise<Record<string, string>[]> => {
  const { maxRows, onProgress } = options || {};

  return new Promise((resolve, reject) => {
    const allRows: Record<string, string>[] = [];
    let rowsProcessed = 0;
    let hasError = false;

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      quoteChar: '"',
      escapeChar: '"',
      delimiter: ',',
      transformHeader: (header: string) => header.trim(),
      transform: (value: string) => value.trim(),
      chunk: (results, parser) => {
        if (hasError) {
          parser.abort();
          return;
        }

        if (results.errors && results.errors.length > 0) {
          hasError = true;
          parser.abort();
          const errorMessage = i18n.t(
            'modelPortfolios:importCsv.errors.failedToParse',
            {
              reason: results.errors[0].message,
            },
          );
          reject(new Error(errorMessage));
          return;
        }

        const chunk = results.data as Record<string, string>[];
        const sanitizedChunk = chunk.map((row) => sanitizeCsvRow(row));

        rowsProcessed += sanitizedChunk.length;

        if (maxRows && rowsProcessed > maxRows) {
          hasError = true;
          parser.abort();
          const errorMessage = i18n.t(
            'modelPortfolios:importCsv.errors.maxRowsExceeded',
            {
              maxRows,
              rowsProcessed,
            },
          );
          reject(new Error(errorMessage));
          return;
        }

        allRows.push(...sanitizedChunk);

        if (onProgress) {
          onProgress(rowsProcessed);
        }
      },
      complete: () => {
        if (hasError) return;

        if (allRows.length === 0) {
          reject(new Error(i18n.t('modelPortfolios:importCsv.errors.noData')));
          return;
        }

        resolve(allRows);
      },
      error: (error) => {
        if (!hasError) {
          const errorMessage = i18n.t(
            'modelPortfolios:importCsv.errors.failedToParse',
            {
              reason: error.message,
            },
          );
          reject(new Error(errorMessage));
        }
      },
    });
  });
};

export const validateParsedCsv = (
  file: File,
  rows: Record<string, string>[],
  options: {
    maxRows: number;
    minRows: number;
    uniqueColumn: string;
    maxFileSizeBytes: number;
  },
): void => {
  const validation = validateCsv(file, rows, options);

  if (!validation.valid) {
    throw new Error(validation.error);
  }
};

export const processValidatedRows = (
  rows: Record<string, string>[],
  securityMap: Map<string, FinancialProduct>,
): {
  securities: Security[];
  errors: string[];
  providedTaxRanks: number[];
  missingSecurities: UnrecognizedSecurity[];
  hasErrors: boolean;
} => {
  const {
    securities: newSecurities,
    errors,
    providedTaxRanks,
    missingSecurities,
  } = processCsvRows(rows, securityMap);

  const taxRankError = validateTaxRanks(providedTaxRanks);
  if (taxRankError) {
    errors.push(taxRankError);
  }

  assignMissingTaxRanks(newSecurities, providedTaxRanks);

  return {
    securities: newSecurities,
    errors,
    providedTaxRanks,
    missingSecurities,
    hasErrors: errors.length > 0,
  };
};
